import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { AreaTagI } from '../../components/ImageTagger';
import { fetchClaimsImages } from '../asyncThunk/ClaimChartImagesThunk';

interface ClaimChartState {
  infringementDetectionId: string;
  newImages: {
    claimSectionId: string;
    imageId: string;
    imageFile: any;
    imageUrl: string;
  }[];
  imageLoading: boolean;
  images: { imageId: string; imageUrl: string }[];
  imagesFromSearchHistory: { imageId: string; imageUrl: string }[];
  imageTags: { imageId: string; tags: { tagName: string; tagId: string }[] }[];
  imageAreaTags: { imageId: string; areas: AreaTagI[] }[];
  imageAreaTagsTemp: { imageId: string; areas: AreaTagI[] }[];
  claimSectionImageTags: {
    imageId: string;
    tags: { tagName: string; tagId: string }[];
  }[];
  claimSectionImageAreaTags: {
    imageId: string;
    areas: AreaTagI[];
  }[];
}

const initialState: ClaimChartState = {
  imageLoading: false,
  infringementDetectionId: '',
  newImages: [],
  images: [],
  imagesFromSearchHistory: [],
  imageTags: [],
  imageAreaTags: [],
  imageAreaTagsTemp: [],
  claimSectionImageTags: [],
  claimSectionImageAreaTags: [],
};

const claimChartSlice = createSlice({
  name: 'claimChart',
  initialState,
  reducers: {
    setInfringementDetectionId: (state, action: PayloadAction<string>) => {
      state.infringementDetectionId = action.payload;
    },
    setNewImages: (
      state,
      action: PayloadAction<{
        claimSectionId: string;
        imageId: string;
        imageFile: any;
        imageUrl: string;
      }>,
    ) => {
      state.newImages.push(action.payload);
    },
    clearNewImages: state => {
      state.newImages = [];
    },
    removeNewImage: (
      state,
      action: PayloadAction<{
        imageId: string;
      }>,
    ) => {
      state.newImages = state.newImages.filter(image => image.imageId !== action.payload.imageId);
    },
    setClaimImages: (state, action: PayloadAction<{ imageId: string; imageUrl: string }>) => {
      if (!state.images.find(image => image.imageId === action.payload.imageId)) {
        state.images = [...state.images, action.payload];
      }
    },
    setClaimImagesFromSearchHistory: (state, action: PayloadAction<{ imageId: string; imageUrl: string }[]>) => {
      state.imagesFromSearchHistory = action.payload;
    },
    removeClaimImage: (state, action: PayloadAction<string>) => {
      state.images = state.images.filter(img => img.imageId !== action.payload);
    },
    setClaimImageTags: (
      state,
      action: PayloadAction<{
        imageId: string;
        tags: { tagName: string; tagId: string }[];
      }>,
    ) => {
      const targetImage = state.imageTags.find(tag => tag.imageId === action.payload.imageId);

      if (targetImage) {
        targetImage.tags = action.payload.tags;
      } else {
        state.imageTags.push(action.payload);
      }
    },
    setClaimImageAreaTags: (
      state,
      action: PayloadAction<{
        imageId: string;
        area: AreaTagI;
      }>,
    ) => {
      const targetImage = state.imageAreaTags.find(tag => tag.imageId === action.payload.imageId);
      if (targetImage) {
        const area = targetImage.areas.find(area => area.id === action.payload.area.id);
        if (area) {
          area.tags = action.payload.area.tags;
        } else {
          targetImage.areas.push(action.payload.area);
        }
      } else {
        state.imageAreaTags.push({
          imageId: action.payload.imageId,
          areas: [action.payload.area],
        });
      }
    },
    setClaimImageAreaTagsTemp: (
      state,
      action: PayloadAction<{
        imageId: string;
        area: AreaTagI;
      }>,
    ) => {
      const targetImage = state.imageAreaTagsTemp.find(tag => tag.imageId === action.payload.imageId);
      if (targetImage) {
        const area = targetImage.areas.find(area => area.id === action.payload.area.id);
        if (area) {
          area.tags = action.payload.area.tags;
        } else {
          targetImage.areas.push(action.payload.area);
        }
      } else {
        state.imageAreaTagsTemp.push({
          imageId: action.payload.imageId,
          areas: [action.payload.area],
        });
      }
    },
    clearClaimImageAreaTagsTemp: state => {
      state.imageAreaTagsTemp = [];
    },
    setClaimSectionImageTags: (
      state,
      action: PayloadAction<{
        imageId: string;
        tags: { tagName: string; tagId: string }[];
      }>,
    ) => {
      const targetImage = state.imageTags.find(tag => tag.imageId === action.payload.imageId);

      if (targetImage) {
        targetImage.tags = action.payload.tags;
      } else {
        state.claimSectionImageTags.push(action.payload);
      }
    },
    setClaimSectionImageAreaTags: (
      state,
      action: PayloadAction<{
        imageId: string;
        area: AreaTagI;
      }>,
    ) => {
      const targetImage = state.imageAreaTags.find(tag => tag.imageId === action.payload.imageId);
      if (targetImage) {
        const area = targetImage.areas.find(area => area.id === action.payload.area.id);
        if (area) {
          area.tags = action.payload.area.tags;
        } else {
          targetImage.areas.push(action.payload.area);
        }
      } else {
        state.claimSectionImageAreaTags.push({
          imageId: action.payload.imageId,
          areas: [action.payload.area],
        });
      }
    },
    createClaimArea: (
      state,
      action: PayloadAction<{
        imageId: string;
        area: AreaTagI;
      }>,
    ) => {
      const targetImage = state.imageAreaTagsTemp.find(tag => tag.imageId === action.payload.imageId);
      if (targetImage) {
        targetImage.areas.push(action.payload.area);
      } else {
        state.imageAreaTagsTemp.push({
          imageId: action.payload.imageId,
          areas: [action.payload.area],
        });
      }
    },
    changeClaimAreaColor: (
      state,
      action: PayloadAction<{
        imageId: string;
        areaId: string;
        color: string;
        temp: boolean;
      }>,
    ) => {
      const targetImage = state.imageAreaTags.find(tag => tag.imageId === action.payload.imageId);
      const targetImageTemp = state.imageAreaTagsTemp.find(tag => tag.imageId === action.payload.imageId);

      if (action.payload.temp) {
        if (targetImageTemp) {
          const area = targetImageTemp.areas.find(area => area.id === action.payload.areaId);
          if (area) {
            area.color = action.payload.color;
          }
        }
      } else {
        if (targetImage) {
          const area = targetImage.areas.find(area => area.id === action.payload.areaId);
          if (area) {
            area.color = action.payload.color;
          }
        }
      }
    },
    deleteClaimAreaFromImage: (
      state,
      action: PayloadAction<{
        imageId: string;
        areaId: string;
      }>,
    ) => {
      const targetImage = state.imageAreaTags.find(tag => tag.imageId === action.payload.imageId);
      if (targetImage) {
        targetImage.areas = targetImage.areas.filter(area => area.id !== action.payload.areaId);
      }
    },
    deleteClaimTempAreaFromImage: (
      state,
      action: PayloadAction<{
        imageId: string;
        areaId: string;
      }>,
    ) => {
      const targetImage = state.imageAreaTagsTemp.find(tag => tag.imageId === action.payload.imageId);
      if (targetImage) {
        targetImage.areas = targetImage.areas.filter(area => area.id !== action.payload.areaId);
      }
    },
    resetClaimChartSlice: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(fetchClaimsImages.pending, state => {
        state.imageLoading = true;
      })
      .addCase(fetchClaimsImages.fulfilled, state => {
        state.imageLoading = false;
      })
      .addCase(fetchClaimsImages.rejected, state => {
        state.imageLoading = false;
      });
  },
});

export default claimChartSlice.reducer;

// Action creators are generated for each case reducer function
export const {
  setInfringementDetectionId,
  setNewImages,
  setClaimImages,
  clearNewImages,
  removeNewImage,

  setClaimImageTags,
  setClaimImageAreaTags,
} = claimChartSlice.actions;
